import React, { useState } from "react";
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  TextInput,
  Box,
  Button,
  Select,
} from "@mantine/core";
import { IconChevronLeft, IconPlus, IconTrash } from "@tabler/icons-react";
import CustomRichTextEditor from "../../common/RichTextEditor";
import {
  hasFieldError,
  getFieldErrorMessage,
  getInputStyles,
  getLabelStyles,
  getLabelText,
} from "../utils/validation";
import {
  ICON_OPTIONS,
  DEFAULT_ICON,
  getIconComponent,
} from "../utils/highlightIcons";

const COLOR_BORDER_LIGHT = "#dee2e6";
const COLOR_BACKGROUND_LIGHT = "#f8f9fa";

interface HighlightItem {
  id: number;
  icon?: string;
  title?: string;
  body?: string;
}

interface HighlightsContent {
  sectionName?: string;
  items?: HighlightItem[];
}

interface WebsiteGeneratorHighlightsProps {
  highlightsContent?: HighlightsContent;
  onContentChange: (content: HighlightsContent) => void;
  onBack: () => void;
  requiredValidation?: { [field: string]: boolean };
  missingFields?: string[];
}

const WebsiteGeneratorHighlights: React.FC<WebsiteGeneratorHighlightsProps> = ({
  highlightsContent,
  onContentChange,
  onBack,
  requiredValidation = {},
  missingFields = [],
}) => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const items = highlightsContent?.items || [];

  // Check if items field has error (when enabled but empty)
  const hasItemsError =
    hasFieldError("items", requiredValidation, items) ||
    missingFields.includes("service_highlights.highlights");

  const handleSectionNameChange = (value: string) => {
    if (value.length > 100) {
      setValidationErrors((prev) => ({
        ...prev,
        sectionName: "Section name cannot exceed 100 characters",
      }));
      return;
    }
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.sectionName;
      return next;
    });
    onContentChange({
      ...(highlightsContent || {}),
      sectionName: value,
    });
  };

  const handleItemChange = (
    itemId: number,
    field: keyof HighlightItem,
    value: string,
  ) => {
    const updatedItems = items.map((item) =>
      item.id === itemId ? { ...item, [field]: value } : item,
    );
    onContentChange({
      ...(highlightsContent || {}),
      items: updatedItems,
    });
  };

  const handleAddItem = () => {
    if (items.length >= 4) {
      setValidationErrors((prev) => ({
        ...prev,
        items: "Maximum 4 items allowed",
      }));
      return;
    }
    const newId =
      items.length > 0 ? Math.max(...items.map((i) => i.id)) + 1 : 1;
    const newItems = [
      ...items,
      {
        id: newId,
        icon: DEFAULT_ICON,
        title: "",
        body: "",
      },
    ];
    onContentChange({
      ...(highlightsContent || {}),
      items: newItems,
    });
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.items;
      return next;
    });
  };

  const handleRemoveItem = (itemId: number) => {
    if (items.length <= 1) {
      setValidationErrors((prev) => ({
        ...prev,
        items: "Minimum 1 item required",
      }));
      return;
    }
    const updatedItems = items.filter((item) => item.id !== itemId);
    onContentChange({
      ...(highlightsContent || {}),
      items: updatedItems,
    });
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.items;
      return next;
    });
  };

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between" align="center">
        <ActionIcon variant="subtle" onClick={onBack}>
          <IconChevronLeft size={16} />
        </ActionIcon>
        <Text fw={600} size="lg">
          Highlights
        </Text>
      </Group>

      {/* Section Name */}
      <Box>
        <Text
          size="sm"
          fw={500}
          mb="xs"
          style={getLabelStyles(
            "sectionName",
            requiredValidation,
            highlightsContent?.sectionName,
          )}
        >
          {getLabelText(
            "Section Name",
            "sectionName",
            requiredValidation,
            highlightsContent?.sectionName,
          )}
        </Text>
        <TextInput
          placeholder="Highlights"
          value={highlightsContent?.sectionName || ""}
          onChange={(event) =>
            handleSectionNameChange(event.currentTarget.value)
          }
          error={getFieldErrorMessage(
            "sectionName",
            requiredValidation,
            validationErrors,
            highlightsContent?.sectionName,
          )}
          maxLength={100}
          styles={getInputStyles(
            "sectionName",
            requiredValidation,
            missingFields,
            undefined,
            highlightsContent?.sectionName,
          )}
        />
      </Box>

      {/* Items */}
      <Box>
        <Group justify="space-between" mb="xs">
          <Text
            size="sm"
            fw={500}
            style={hasItemsError ? { color: "red" } : undefined}
          >
            Items ({items.length}/4) {hasItemsError && "*"}
          </Text>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconPlus size={14} />}
            onClick={handleAddItem}
            disabled={items.length >= 4}
          >
            Add Item
          </Button>
        </Group>
        {hasItemsError && (
          <Text size="xs" c="red" mb="xs">
            At least one highlight item is required
          </Text>
        )}
        {validationErrors.items && (
          <Text size="xs" c="red" mb="xs">
            {validationErrors.items}
          </Text>
        )}

        <Stack gap="md">
          {items.map((item) => (
            <Box
              key={item.id}
              p="md"
              style={{
                border: `1px solid ${COLOR_BORDER_LIGHT}`,
                borderRadius: "8px",
                backgroundColor: COLOR_BACKGROUND_LIGHT,
              }}
            >
              <Group justify="space-between" mb="md">
                <Text size="sm" fw={500}>
                  Item {items.indexOf(item) + 1}
                </Text>
                <ActionIcon
                  variant="subtle"
                  color="red"
                  onClick={() => handleRemoveItem(item.id)}
                  disabled={items.length <= 1}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>

              {/* Icon */}
              <Box mb="sm">
                <Text size="sm" fw={500} mb="xs">
                  Icon
                </Text>
                <Select
                  value={item.icon || DEFAULT_ICON}
                  onChange={(value) =>
                    handleItemChange(item.id, "icon", value || DEFAULT_ICON)
                  }
                  data={ICON_OPTIONS.map((opt) => ({
                    value: opt.value,
                    label: opt.label,
                  }))}
                  leftSection={getIconComponent(item.icon || DEFAULT_ICON)}
                />
              </Box>

              {/* Title */}
              <Box mb="sm">
                <Text size="sm" fw={500} mb="xs">
                  Title
                </Text>
                <TextInput
                  placeholder="Enter title..."
                  value={item.title || ""}
                  onChange={(event) =>
                    handleItemChange(
                      item.id,
                      "title",
                      event.currentTarget.value,
                    )
                  }
                  maxLength={100}
                />
              </Box>

              {/* Body */}
              <Box>
                <CustomRichTextEditor
                  label="Body Text"
                  placeholder="Enter body text..."
                  value={item.body || ""}
                  onChange={(htmlValue) =>
                    handleItemChange(item.id, "body", htmlValue)
                  }
                  minHeight={120}
                />
              </Box>
            </Box>
          ))}
        </Stack>
      </Box>
    </Stack>
  );
};

export default WebsiteGeneratorHighlights;
